import { z } from 'zod';
import { ReplyTemplateId } from './templates.js';
/**
 * Input for `POST /api/conversations/:id/reply`. Two distinct modes:
 *
 *   1. **Text reply** (`bodyText`) — agent typed a quick message. Server
 *      auto-paragraphs it and wraps in the branded email layout.
 *   2. **Template send** (`templateId`) — agent picked a pre-made template.
 *      Server loads the template body + subject from the DB and wraps in
 *      the layout. The agent never sees the template HTML; no editing.
 *
 * `bodyHtml` is an escape hatch for admins who need to send arbitrary HTML
 * (scripted via the API). The composer UI never sends it.
 *
 * Exactly one of `bodyText` / `templateId` / `bodyHtml` must be present.
 * `subject` is optional — when omitted, the server uses the template's
 * subject (if picking a template), otherwise the conversation's subject
 * with `Re: ` prefix.
 */
export const PostReplyRequest = z
    .object({
    subject: z.string().max(500).nullable().optional(),
    bodyText: z.string().max(200_000).optional(),
    bodyHtml: z.string().max(200_000).optional(),
    templateId: ReplyTemplateId.optional(),
})
    .refine((v) => {
    const provided = [
        v.bodyText != null && v.bodyText.trim().length > 0,
        v.bodyHtml != null && v.bodyHtml.trim().length > 0,
        v.templateId != null,
    ].filter(Boolean).length;
    return provided === 1;
}, {
    message: 'Exactly one of bodyText, templateId, or bodyHtml is required',
    path: ['bodyText'],
});
/**
 * Typed error codes the reply endpoint may return (HTTP 4xx / 502). The
 * client maps these to toasts / retry affordances. Kept as a literal union
 * so additions are cheap.
 */
export const ReplyErrorCode = z.enum([
    'no_recipient',
    'channel_cannot_send',
    'channel_not_found',
    'send_failed_retryable',
    'send_failed_permanent',
    // An idempotent retry collapsed onto a prior attempt for the same key that is
    // currently FAILED and was NOT re-dispatched (still within the reconcile/
    // re-dispatch staleness window). This call did NOT send — callers must treat
    // it as a non-success (no 201, no success-only hooks). The scheduled-sender
    // treats it as a benign deferred duplicate (await reclaim, no attempt burn).
    'duplicate_send_pending',
    // The recipient is on the email blacklist and this was an AUTOMATIC send (a
    // pipeline action, via `apps/worker/src/adapters/reply-port.ts`). Never
    // produced by an operator's own reply: the blacklist restricts what the system
    // sends by itself, not what a human decides to answer. The pipeline treats it
    // as a genuine send failure, so the step is `failed` and the node's `escalated`
    // edge hands the conversation to a person.
    'recipient_blacklisted',
]);
//# sourceMappingURL=reply.js.map